import { Routes, Route, Navigate } from "react-router-dom";
import { Suspense } from "react";
import LoginEntry from "./pages/Auth/LoginEntry";
import Login from "./pages/Auth/Login";
import AuthCallback from "./pages/Auth/AuthCallback";
import OTPVerify from "./pages/Auth/OTPVerify";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import OnboardingStep1 from "./pages/Auth/OnboardingStep1";
import OnboardingStep2 from "./pages/Auth/OnboardingStep2";
import ReviewStatusScreen from "./pages/Auth/ReviewStatusScreen";
import DashboardLayout from "./components/layout/DashboardLayout";
import V2DashboardLayout from "./components/dashboard-v2/V2DashboardLayout";
import { dashboardRoutes } from "./routes/DashboardRoutes";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import ProtectedRoute, {
  GuestRoute,
  RegisterRoute,
} from "./components/ProtectedRoute";
import { SnackbarProvider } from "notistack";
import Health from "./components/Health";
import ErrorBoundary from "./components/ErrorBoundary";
import generateRoutes from "./utils/generateRoutes";
import useClarity from "./hooks/useClarity";
import BrandAdminLogin from "./pages/Auth/BrandAdminLogin";
import DefaultRedirect from "./components/DefaultRedirectOnAppLoad";
import ScrollToTop from "./components/ScrollToTop";
import { FeedbackProvider } from "./context/FeedbackContext";
import { FeedbackBugModal } from "./components/FeedbackBug";
import { OnboardingProvider } from "./context/OnboardingContext";

const App = () => {
  useClarity();
  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={3000}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Suspense fallback={<div>Loading...</div>}>
          <ErrorBoundary>
            <ScrollToTop />
            <FeedbackProvider>
              <OnboardingProvider>
                <Routes>
                  {/* Public Routes */}
                  <Route element={<GuestRoute />}>
                    <Route path="/login" element={<LoginEntry />} />
                    <Route path="/login/password" element={<Login />} />

                    <Route
                      path="/brand-admin-login"
                      element={<BrandAdminLogin />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                  </Route>

                  {/* Auth callback + OTP should not be guest-guarded; they handle
                      intermediate authenticated state and own their redirects. */}
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/login/otp" element={<OTPVerify />} />
                  <Route element={<RegisterRoute />}>
                    <Route path="/register" element={<OnboardingStep1 />} />
                    <Route
                      path="/register/brand"
                      element={<OnboardingStep2 />}
                    />
                    <Route
                      path="/register/status"
                      element={<ReviewStatusScreen />}
                    />
                    <Route
                      path="/register/waitlisted"
                      element={<Navigate to="/register/status" replace />}
                    />
                    <Route
                      path="/register/rejected"
                      element={<Navigate to="/register/status" replace />}
                    />
                    <Route
                      path="/register/success"
                      element={<Navigate to="/register/status" replace />}
                    />
                  </Route>

                  <Route path="/health" element={<Health />} />

                  {/* Protected Routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<V2DashboardLayout />}>
                      <Route path="/" element={<DefaultRedirect />} />
                      <Route element={<DashboardLayout embedded />}>
                        {generateRoutes(dashboardRoutes)}
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </OnboardingProvider>
              <FeedbackBugModal />
            </FeedbackProvider>
          </ErrorBoundary>
        </Suspense>
      </LocalizationProvider>
    </SnackbarProvider>
  );
};

export default App;
